import { ClientSecretExpirationBanner } from '../../ClientSecretExpirationBanner/ClientSecretExpirationBanner'
import { microsoftResellerAuthorizationValidationSchema } from '../../../common/utils/validateMicrosoftResellerAuthorization'
import { ProductNameSection } from '../../../common/components/ProductNameSection/ProductNameSection'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { Form, Formik } from 'formik'
import { useStyles } from './ResellerAuthorizationCreation.styles'
import { ResellerAuthorizationCreationFooter } from './ResellerAuthorizationCreationFooter/ResellerAuthorizationCreationFooter'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Product } from '../../../modules/productsListing/types'
import type { CloudMarketplace } from '../../../types'
import { Grid } from '@mui/material'
import { ResellerAuthorizationCreationForm } from './ResellerAuthorizationCreationForm/ResellerAuthorizationCreationForm'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAccountId } from '@labrav/common'
import { get, isEmpty } from 'lodash'
import { useDispatch, useSelector } from '../../../store'
import { getProducts } from '../../../modules/productsListing/action'
import {
  clearResaleAuthorizationInCreation,
  getCurrentResaleAuthorizationInCreation,
  getDefaultResaleAuthorizationInCreation,
  setResaleAuthorizationInCreation,
  submitResaleAuthorization,
} from '../../../modules/resaleAuthorization/action'
import { ResaleAuthorizationWithErrors } from '../../../modules/resaleAuthorization/types'
import { LoadingLogo } from '@labrav/react-components'
import {
  LoadingTypes,
  isLoading as Loading,
  useCurrentCloudProvider,
} from '@labrav/common'
import { getMarketplaceReferralTemplateSalesforce } from '../../../modules/marketplaceMappingManager/action'
import { CRMTypes } from '../../../utils/constants'
import clsx from 'clsx'
import { useCommonStyles } from '../../../common/styles/common.styles'
import type { ResellerAuthorizationCreationProps } from '../types'

export const ResellerAuthorizationCreation = ({
  crmOpportunityId,
  externalBaseURL,
  search,
  users,
  isSalesforceContext,
}: ResellerAuthorizationCreationProps) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const { productId = '', resaleAuthorizationId = '' } = useParams<{
    productId: string
    resaleAuthorizationId?: string
  }>()
  const {
    accountId: routeAccountId,
    cloud: routeCloud,
    partnerId,
  } = useParams<{
    cloud: string
    accountId: string
    partnerId?: string
  }>()
  const location = useLocation()
  const isSalesforceRoute = location.pathname.includes('/salesforce')
  const isHubspotAzureRoute =
    location.pathname.includes('/external-apps/hubspot/') &&
    routeCloud?.toUpperCase() === 'AZURE'
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId =
    routeAccountId ||
    (isSalesforceRoute ? partnerId : '') ||
    fallbackAccountId ||
    ''
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const crmObjectId = new URLSearchParams(search)
    .get('hs_object_id')
    ?.toLowerCase()

  const templateId = new URLSearchParams(search)
    .get('templateId')
    ?.toLowerCase()

  const crmId = useSelector<string | undefined>(
    state => state.AccountData.user.partnerData?.crmId || ''
  )
  const isDefaultOfferDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_REFERRAL_FORM_DATA)
  )
  const isDefaultOfferValuesLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GET_DEFAULT_OFFERS)
  )
  const fetchCurrentTemplateLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
  )
  const loadingSaveAsDraft = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.DRAFT_SAVE_RESALE_AUTHORIZATION)
  )
  const offerSubmitloading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.SAVE_RESALE_AUTHORIZATION)
  )
  const selectedCloudTypeProductListing = useCurrentCloudProvider({
    microsoftResellerAuthorization: true,
  })
  const { products } = useFlyOutProductsBasedOnSelectedCloud({
    microsoftResellerAuthorization: true,
  })
  const product =
    products.find(product => product.productId === productId) || ({} as Product)

  const resaleAuthorization = useSelector<ResaleAuthorizationWithErrors>(
    state =>
      state.resaleAuthorizationsInCreation.currentResaleAuthorizationInCreation[
        productId
      ] || ({} as ResaleAuthorizationWithErrors)
  )

  const fetchedSalesforceDefaultsKey = useRef<string | null>(null)
  const [templatePrefillFailed, setTemplatePrefillFailed] = useState(false)
  const [templatePrefillReady, setTemplatePrefillReady] = useState(false)
  const hasProducts = products.length > 0

  useEffect(() => {
    const crmObjectIdToUse = crmObjectId || crmOpportunityId
    if (
      (!isSalesforceRoute && !isHubspotAzureRoute) ||
      !crmId ||
      !crmObjectIdToUse ||
      !templateId ||
      !accountId ||
      !hasProducts
    ) {
      return
    }

    const fetchKey = `${accountId}:${productId}:${templateId}:${crmId}:${crmObjectIdToUse}`
    if (fetchedSalesforceDefaultsKey.current === fetchKey) {
      return
    }
    fetchedSalesforceDefaultsKey.current = fetchKey
    setTemplatePrefillFailed(false)
    setTemplatePrefillReady(false)

    const init = async () => {
      const templateLoaded = await dispatch(
        getMarketplaceReferralTemplateSalesforce(
          crmId,
          accountId,
          templateId,
          undefined,
          isHubspotAzureRoute ? CRMTypes.hubspot : undefined,
          isHubspotAzureRoute ? 'resale_auth' : undefined
        )
      )
      if (!templateLoaded && isHubspotAzureRoute) {
        setTemplatePrefillFailed(true)
        return
      }
      const defaultsLoaded = await dispatch(
        getDefaultResaleAuthorizationInCreation({
          productId,
          crmOpportunityId,
          crmId,
          crmObjectId: crmObjectIdToUse,
          templateId,
          accountId,
          crmType: isHubspotAzureRoute
            ? CRMTypes.hubspot
            : CRMTypes.salesforce,
          cloudMarketplace: isHubspotAzureRoute
            ? (routeCloud?.toUpperCase() as CloudMarketplace)
            : undefined,
        })
      )
      if (isHubspotAzureRoute && !defaultsLoaded) {
        setTemplatePrefillFailed(true)
      } else if (isHubspotAzureRoute) {
        setTemplatePrefillReady(true)
      }
    }

    void init()
  }, [
    crmId,
    crmObjectId,
    productId,
    crmOpportunityId,
    accountId,
    isSalesforceRoute,
    isHubspotAzureRoute,
    templateId,
    hasProducts,
    selectedCloudTypeProductListing,
    dispatch,
  ])

  useEffect(() => {
    return () => {
      if (isSalesforceRoute || isHubspotAzureRoute) {
        dispatch(clearResaleAuthorizationInCreation(productId))
      }
    }
  }, [dispatch, isSalesforceRoute, isHubspotAzureRoute, productId])

  useEffect(() => {
    if (accountId && selectedCloudTypeProductListing) {
      if (isEmpty(products)) {
        dispatch(getProducts(accountId, selectedCloudTypeProductListing))
      } else if (
        !isHubspotAzureRoute ||
        resaleAuthorizationId ||
        !templateId
      ) {
        dispatch(
          getCurrentResaleAuthorizationInCreation({
            partnerId: accountId,
            productId,
            resaleAuthorizationId: resaleAuthorizationId || '',
            crmOpportunityId,
            cloud: selectedCloudTypeProductListing,
          })
        )
      }
    }
  }, [
    accountId,
    products,
    productId,
    resaleAuthorizationId,
    crmOpportunityId,
    selectedCloudTypeProductListing,
    isHubspotAzureRoute,
    templateId,
  ])

  const baseURL = useMemo(() => {
    if (isSalesforceRoute) {
      return `/external-apps/salesforce/partners/${accountId}/flyout/products-listing/${productId}`
    }
    return (
      externalBaseURL ||
      (routeCloud && accountId
        ? `/flyout/${routeCloud}/${accountId}/products-listing/${productId}`
        : `/flyout/products-listing/${productId}`)
    )
  }, [externalBaseURL, productId, isSalesforceRoute, accountId, routeCloud])

  const submitCallBack = ({
    id,
    isDraft,
    resaleAuthorization: resaleAuthorizationData,
  }: {
    id?: string
    isDraft?: boolean
    resaleAuthorization?: Record<string, unknown>
  }) => {
    if (resaleAuthorizationData) {
      dispatch(
        setResaleAuthorizationInCreation({
          data: resaleAuthorizationData,
          errors: {},
          noOfTimesSaved: 2,
          productId,
        })
      )
    } else if (id && isDraft) {
      const updatedSearch = new URLSearchParams(search)
      updatedSearch.delete('templateId')
      navigate({
        pathname: `${baseURL}/resale-authorizations/${id}/edit`,
        search: updatedSearch.toString(),
      })
    } else if (!isDraft) {
      navigate({
        pathname: `${baseURL}/resale-authorizations/${id}/details`,
        search,
      })
    }
  }

  const validationSchema = useMemo(
    () =>
      microsoftResellerAuthorizationValidationSchema(
        !!isSalesforceContext || !!externalBaseURL
      ),
    [externalBaseURL, isSalesforceContext]
  )

  const canShowResellerAuthorization =
    !isEmpty(resaleAuthorization) &&
    (!isHubspotAzureRoute || !templateId || templatePrefillReady) &&
    !fetchCurrentTemplateLoading &&
    !isDefaultOfferDataLoading &&
    !isDefaultOfferValuesLoading

  // Match Azure private offer: no app header in Salesforce/external → sticky at 0
  const hasExternalBaseURL = !!externalBaseURL
  const tabTop = hasExternalBaseURL ? '0px' : '64px'
  const offset = hasExternalBaseURL ? 150 : 260

  if (isHubspotAzureRoute && templatePrefillFailed) {
    return <div role="alert">Unable to load the selected template. Please try again.</div>
  }

  if (!canShowResellerAuthorization)
    return (
      <div className={commonClasses.loadingWrapper}>
        <LoadingLogo />
      </div>
    )

  return (
    <Formik
      initialValues={{
        ...resaleAuthorization,
      }}
      validationSchema={validationSchema}
      validateOnMount={true}
      enableReinitialize
      onSubmit={valuesData => {
        if (accountId && productId) {
          dispatch(
            submitResaleAuthorization({
              partnerId: accountId,
              productId,
              resaleAuthorization: valuesData,
              saveAsDraft: false,
              crmObjectLink: '',
              crmOpportunityId,
              submitCallBack,
            })
          )
        }
      }}
    >
      {formikProps => {
        const footerWidth = '100%'
        const handleSaveAsDraft = useCallback(
          ({ saveAsDraft }: { saveAsDraft: boolean }) => {
            if (accountId) {
              dispatch(
                submitResaleAuthorization({
                  partnerId: accountId,
                  productId,
                  resaleAuthorization: formikProps?.values,
                  saveAsDraft,
                  crmObjectLink: '',
                  crmOpportunityId,
                  submitCallBack,
                })
              )
            }
          },
          [formikProps, accountId, productId, crmOpportunityId]
        )
        const handleSubmit = async () => {
          await formikProps.submitForm()
        }
        const disableSaveAsDraft = !isEmpty(
          get((formikProps.errors as Record<string, unknown>) || {}, 'plans')
        )

        return (
          <Form data-testid="reseller-authorization-creation">
            <Grid container spacing={4}>
              <Grid size={9}>
                <div
                  className={clsx(
                    classes.root,
                    isSalesforceRoute ? classes.salesforceWrapper : ''
                  )}
                  data-testid="formik-Container"
                >
                  <ClientSecretExpirationBanner
                    variant="banner"
                    accountId={accountId}
                  />
                  <ProductNameSection product={product} />
                  <ResellerAuthorizationCreationForm
                    product={product}
                    tabTop={tabTop}
                    offset={offset}
                    users={users}
                    externalBaseURL={externalBaseURL}
                    isSalesforceContext={isSalesforceContext}
                  />
                </div>
              </Grid>
            </Grid>

            <ResellerAuthorizationCreationFooter
              footerWidth={footerWidth}
              handleSaveAsDraft={handleSaveAsDraft}
              handleSubmit={handleSubmit}
              loadingSaveAsDraft={loadingSaveAsDraft}
              offerSubmitloading={offerSubmitloading}
              disableSaveAsDraft={disableSaveAsDraft}
            />
          </Form>
        )
      }}
    </Formik>
  )
}
